import { useState, useRef, useMemo } from 'react';
import {
  Box,
  Popover,
  Typography,
  InputBase,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloseIcon from '@mui/icons-material/Close';

// ------------------------------------------------------------
// SourcePicker — انتخاب منبع بلیط
//
// @param {*}        value       مقدار انتخاب‌شده
// @param {Function} onChange    کال‌بک تغییر مقدار
// @param {Array}    options     آپشن‌ها:
//   [
//     {
//       value: 'SYSTEM_ASSIGN' | 123 | null,
//       title: 'تخصیص بلیط منبع توسط سیستم',
//       subtitle: 'QR-00012 | BARCODE-00012',  // اختیاری
//       id: 'AUTO' | '23653',                  // اختیاری
//       isSystem: true | false,                // اختیاری
//     },
//     ...
//   ]
// @param {String}   placeholder
// @param {Boolean}  disabled
// ------------------------------------------------------------

function SourcePicker({
  value,
  onChange,
  options = [],
  placeholder = 'انتخاب منبع',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef(null);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const isSystemSelected = !!selected?.isSystem;

  // ------------------------------------------------------------
  // فیلتر بر اساس جستجو
  // ------------------------------------------------------------
  const filtered = options.filter((o) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      String(o.title ?? '').toLowerCase().includes(s) ||
      String(o.subtitle ?? '').toLowerCase().includes(s) ||
      String(o.id ?? '').toLowerCase().includes(s)
    );
  });

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <>
      {/* ------------------------------------------------------------
          Trigger
          ------------------------------------------------------------ */}
      <Box
        ref={anchorRef}
        onClick={() => !disabled && setOpen(true)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.9,
          minHeight: 42,
          border: '1px solid',
          borderColor: isSystemSelected
            ? '#c7d2fe'
            : open
              ? '#475569'
              : '#e2e8f0',
          borderRadius: 1.5,
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: disabled
            ? '#f1f5f9'
            : isSystemSelected
              ? '#fafaff'
              : '#fff',
          transition: 'all .15s',
          userSelect: 'none',
          boxShadow: open ? '0 0 0 3px rgba(71,85,105,0.12)' : 'none',
          '&:hover': !disabled && {
            borderColor: open
              ? '#475569'
              : isSystemSelected
                ? '#a5b4fc'
                : '#cbd5e1',
            boxShadow: open
              ? '0 0 0 3px rgba(71,85,105,0.12)'
              : '0 2px 8px -4px rgba(15,23,42,0.08)',
          },
        }}
      >
        {/* آیکون */}
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isSystemSelected
              ? '#eef2ff'
              : selected
                ? '#f1f5f9'
                : '#f8fafc',
            flexShrink: 0,
          }}
        >
          <AssignmentTurnedInIcon
            sx={{
              fontSize: 15,
              color: isSystemSelected
                ? '#4338ca'
                : selected
                  ? '#475569'
                  : '#94a3b8',
            }}
          />
        </Box>

        {/* متن انتخاب‌شده یا placeholder */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {selected ? (
            <Typography
              sx={{
                fontSize: 12.5,
                color: isSystemSelected ? '#4338ca' : '#334155',
                fontWeight: isSystemSelected ? 600 : 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontFamily: !selected.subtitle ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace !important' : "inherit"
              }}
            >
              {`${!!selected.id && !isNaN(selected.id) ? selected.id + " | " : ""}${selected.title}`}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>
              {placeholder}
            </Typography>
          )}
        </Box>

        {/* دکمه پاک کردن */}
        {selected && !disabled && (
          <IconButton
            size="small"
            onClick={handleClear}
            sx={{
              width: 20,
              height: 20,
              color: '#94a3b8',
              '&:hover': { color: '#dc2626', background: '#fef2f2' },
            }}
          >
            <CloseIcon sx={{ fontSize: 13 }} />
          </IconButton>
        )}

        {/* فلش */}
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: '#94a3b8',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform .2s',
            flexShrink: 0,
          }}
        />
      </Box>

      {/* ------------------------------------------------------------
          Popover
          ------------------------------------------------------------ */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => {
          setOpen(false);
          setSearch('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            width: anchorRef.current?.offsetWidth || 320,
            minWidth: 300,
            maxWidth: 420,
            borderRadius: 1.5,
            border: '1px solid #e2e8f0',
            boxShadow: '0 12px 32px -12px rgba(15,23,42,0.18)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Search */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderBottom: '1px solid #e2e8f0',
            background: '#fafafa',
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
          <InputBase
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="جستجو..."
            autoFocus
            sx={{
              flex: 1,
              fontSize: 12.5,
              '& input': { p: 0 },
              '& input::placeholder': { color: '#94a3b8' },
            }}
          />
        </Box>

        {/* Options list */}
        <Box sx={{ maxHeight: 280, overflowY: 'auto', p: 0.5 }}>
          {filtered.length === 0 && (
            <Typography
              sx={{
                py: 3,
                textAlign: 'center',
                fontSize: 12,
                color: '#94a3b8',
              }}
            >
              موردی یافت نشد
            </Typography>
          )}

          {filtered.map((opt) => {
            const isSelected = opt.value === value;
            const isSystem = !!opt.isSystem;

            return (
              <Box
                key={String(opt.value)}
                onClick={() => handleSelect(opt)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                  px: 1.2,
                  py: 1,
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: 'background .12s',
                  background: isSelected ? '#f1f5f9' : 'transparent',
                  borderBottom: isSystem ? '1px dashed #e2e8f0' : 'none',
                  mb: isSystem ? 0.5 : 0,
                  pb: isSystem ? 1.2 : 1,
                  '&:hover': {
                    background: isSelected ? '#e2e8f0' : '#f8fafc',
                  },
                }}
              >
                {/* دایره انتخاب */}
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: isSelected ? 'none' : '1.5px solid #cbd5e1',
                    background: isSelected
                      ? isSystem
                        ? '#4338ca'
                        : '#475569'
                      : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'background .15s',
                  }}
                >
                  {isSelected && (
                    <CheckIcon sx={{ fontSize: 12, color: '#fff' }} />
                  )}
                </Box>

                {/* محتوا */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      color: isSystem ? '#4338ca' : '#334155',
                      fontWeight: isSelected ? 600 : isSystem ? 600 : 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      mb: 0.2,
                      fontFamily: !opt.subtitle ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace !important' : "inherit"
                    }}
                  >
                    {opt.title}
                  </Typography>

                  {opt.subtitle && (
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: '#94a3b8',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {opt.subtitle}
                    </Typography>
                  )}
                </Box>

                {/* Badge ID */}
                {opt.id && (
                  <Box
                    sx={{
                      flexShrink: 0,
                      px: 0.8,
                      py: 0.2,
                      borderRadius: 0.8,
                      background: isSystem ? '#eef2ff' : '#f8fafc',
                      border: `1px solid ${isSystem ? '#c7d2fe' : '#e2e8f0'
                        }`,
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 10,
                      fontWeight: 700,
                      color: isSystem ? '#4338ca' : '#64748b',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {opt.id}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
}

export default SourcePicker;